import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  EVENT_TYPES, POKEMON_ROLES, formatRange, parseDate, typeColor,
} from '../lib/format.js';
import { VERDICT } from '../components/Verdict.jsx';
import { RAID_TIER } from './RaidsPage.jsx';

const LIGA = { great: 'Superliga', ultra: 'Hyperliga', master: 'Meisterliga' };

const ROLLEN = { attacker: 'Angreifer', defender: 'Verteidiger', raid_boss: 'Raid-Boss' };

/** Grobe Einordnung des IV-Durchschnitts, damit die Farbe etwas aussagt. */
const ivStufe = (prozent) =>
  prozent >= 98 ? 'S' : prozent >= 90 ? 'A' : prozent >= 80 ? 'B' : prozent >= 66 ? 'C' : 'D';

/** Wie die Quellen heißen sollen — "ei" allein sagt nichts. */
const QUELLE = {
  ei: 'Aus Eiern',
  raid: 'Aus Raids',
  forschung: 'Aus Forschung',
};

export default function PokemonDetailPage() {
  const { slug } = useParams();
  const [mon, setMon] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aktiv = true;
    setMon(null);
    setError(null);
    api.pokemon.get(slug)
      .then((d) => aktiv && setMon(d.pokemon))
      .catch((e) => aktiv && setError(e.message));
    return () => { aktiv = false; };
  }, [slug]);

  if (error) {
    return (
      <>
        <div className="error">{error}</div>
        <p style={{ marginTop: 16 }}><Link to="/pokemon" className="nav__link">← Zur Übersicht</Link></p>
      </>
    );
  }

  if (!mon) return <div className="skeleton" style={{ height: 260 }} />;

  const urteil = VERDICT[mon.verdict];

  // Der Streifen oben stuetzte sich allein auf die Raid-Einstufung und
  // meldete "Noch nicht bewertet", obwohl direkt darunter eine
  // Liga-Einstufung stand. Jetzt gilt das Beste ueber alle Zwecke.
  const ZWECKE = [
    ['great', 'Superliga'], ['ultra', 'Hyperliga'],
    ['master', 'Meisterliga'], ['raid', 'Raids'],
  ];
  const STUFEN = ['S', 'A', 'B', 'C', 'D'];

  const beste = ZWECKE
    .map(([k, label]) => [label, mon.eignung?.[k]])
    .filter(([, w]) => w)
    .sort((a, b) => STUFEN.indexOf(a[1].tier) - STUFEN.indexOf(b[1].tier))[0];
  const jetzt = new Date();
  const kommend = mon.appearances.filter((a) => parseDate(a.endsAt) >= jetzt);
  const vorbei = mon.appearances.filter((a) => parseDate(a.endsAt) < jetzt);

  return (
    <>
      <p style={{ marginBottom: 16 }}><Link to="/pokemon" className="nav__link">← Alle Pokémon</Link></p>

      <header
        className="detail__head"
        style={{ '--card-accent': beste ? 'var(--verdict-strong)' : urteil.farbe }}
      >
        <div className="inline" style={{ marginBottom: 12 }}>
          {beste ? (
            <span className="badge" style={{ '--badge-color': 'var(--verdict-strong)' }}>
              {beste[0]} · Stufe {beste[1].tier}
            </span>
          ) : (
            <span className="badge badge--soft">Nirgends eingestuft</span>
          )}
          {mon.shinyBoostAhead && <span className="badge badge--soft">Shiny-Chance erhöht</span>}
        </div>

        <h1>
          {mon.name}
          {mon.form && <span className="mon-card__form"> {mon.form}</span>}
        </h1>

        <div className="inline" style={{ marginTop: 12 }}>
          <span className="mon__dex">#{String(mon.dexNo).padStart(3, '0')}</span>
          {mon.types.map((t) => (
            <span className="type" key={t} style={{ '--type-color': typeColor(t) }}>{t}</span>
          ))}
        </div>

        <p className="hero__sub" style={{ marginTop: 14 }}>
          {beste
            ? 'Am stärksten in: ' + beste[0] + '.'
            : 'Weder in den Ligen noch für Raids eingestuft.'}
        </p>
      </header>

      {/* Auf einen Blick: wofür taugt die Art? */}
      <section className="section">
        <h2 className="section__title">Wofür taugt es?</h2>

        <div className="eignung">
          <EignungFeld titel="Superliga"   hinweis="bis 1500 WP" wert={mon.eignung?.great} />
          <EignungFeld titel="Hyperliga"   hinweis="bis 2500 WP" wert={mon.eignung?.ultra} />
          <EignungFeld titel="Meisterliga" hinweis="ohne Grenze" wert={mon.eignung?.master} />
          <EignungFeld titel="Raids"       hinweis="als Angreifer" wert={mon.eignung?.raid} raid />
        </div>

        <p className="card__meta" style={{ marginTop: 12 }}>
          Trainerkampf-Werte stammen aus berechneten Ranglisten und decken fast
          den ganzen Katalog ab. Die Raid-Einstufung ist von Hand gepflegt —
          fehlt sie, heißt das nicht, dass die Art nichts taugt.
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">Raid-Bewertung</h2>

        {mon.ratings.length === 0 ? (
          <div className="panel">
            <p>
              Für Raids ist diese Art noch nicht eingestuft. Die
              Raid-Bewertungen sind von Hand gepflegt und decken bislang nur
              einen kleinen Teil des Katalogs ab.
            </p>
          </div>
        ) : (
          <div className="panel">
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Rolle</th>
                  <th>Als Typ</th>
                  <th>Stufe</th>
                  <th>Anmerkung</th>
                </tr>
              </thead>
              <tbody>
                {mon.ratings.map((r, i) => (
                  <tr key={i}>
                    <td>{ROLLEN[r.role] ?? r.role}</td>
                    <td>
                      {r.attackType
                        ? <span className="type" style={{ '--type-color': typeColor(r.attackType) }}>{r.attackType}</span>
                        : <span className="text-faint">—</span>}
                    </td>
                    <td><span className={`tier tier--${r.tier}`}>{r.tier}</span></td>
                    <td className="tabelle__notiz">
                      {r.notes || <span className="text-faint">—</span>}
                      {r.needsEliteTm && <span className="badge badge--soft" style={{ marginLeft: 8 }}>Lade-TM nötig</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {mon.owned?.length > 0 && (
        <section className="section">
          <h2 className="section__title">Deine Exemplare ({mon.owned.length})</h2>
          <div className="mon-grid">
            {mon.owned.map((e, i) => (
              <div className="mon" key={i} style={{ cursor: 'default' }}>
                <div className="spread">
                  <span className="mon__name">WP {e.cp}</span>
                  {e.ivAverage !== null && (
                    <span className={`tier tier--${ivStufe(e.ivAverage)}`}>{e.ivAverage}%</span>
                  )}
                </div>
                <div className="card__meta">
                  {e.ivAttack !== null
                    ? `IV ${e.ivAttack}/${e.ivDefense}/${e.ivStamina}`
                    : 'IV nicht erfasst'}
                  {e.level !== null && ` · Level ${e.level}`}
                </div>
                <div className="inline">
                  {e.isShadow && <span className="badge badge--soft">Crypto</span>}
                  {e.isPurified && <span className="badge badge--soft">Erlöst</span>}
                  {e.isLucky && <span className="badge badge--soft">Glücks</span>}
                  {e.isFavorite && <span className="badge badge--soft">Favorit</span>}
                </div>
                {e.quickMove && (
                  <p className="card__summary">
                    {[e.quickMove, e.chargeMove, e.chargeMove2].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {mon.woher?.length > 0 && (
        <section className="section">
          <h2 className="section__title">Woher bekommst du es?</h2>
          <div className="stack">
            {mon.woher.map((w, i) => (
              <div className="admin-item" key={i}>
                <div>
                  <div className="inline" style={{ marginBottom: 4 }}>
                    <span className="badge badge--soft">{QUELLE[w.quelle] ?? w.quelle}</span>
                    {w.shiny && <span className="badge badge--soft">Shiny möglich</span>}
                    {w.regional && <span className="badge badge--soft">regional begrenzt</span>}
                    {w.form && <span className="badge badge--form">als {w.form}</span>}
                  </div>
                  {w.detail && <p className="card__summary">{w.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {mon.keinAuftritt && (
        <section className="section">
          <h2 className="section__title">Kommende Auftritte</h2>
          <div className="empty">
            <p>Kein Auftritt bekannt.</p>
            <p className="text-faint">
              Das heißt nicht, dass {mon.name} nicht vorkommt — nur, dass in
              den erfassten Events nichts dazu steht. Der Abgleich läuft
              wöchentlich und reicht rund drei Monate voraus.
              {mon.datenstand && (
                <> Stand: {new Date(mon.datenstand.replace(' ', 'T'))
                  .toLocaleDateString('de-DE')}.</>
              )}
            </p>
          </div>
        </section>
      )}

      {mon.raidWindows?.length > 0 && (
        <section className="section">
          <h2 className="section__title">Als Raid-Boss</h2>
          <div className="stack">
            {mon.raidWindows.map((w, i) => {
              const stufe = RAID_TIER[w.tier] ?? { label: w.tier, farbe: 'var(--accent)' };

              return (
                <div className={`admin-item${w.status === 'past' ? ' admin-item--draft' : ''}`} key={i}>
                  <div>
                    <div className="inline" style={{ marginBottom: 4 }}>
                      <span className="badge" style={{ '--badge-color': stufe.farbe }}>{stufe.label}</span>
                      {w.status === 'live' && <span className="badge badge--live">Aktiv</span>}
                      {w.shinyAvailable && <span className="badge badge--soft">Shiny möglich</span>}
                      {/* Andere Form derselben Art — etwa Crypto-Relaxo auf
                          der Seite des gewöhnlichen Relaxo. */}
                      {w.form && (
                        w.formSlug
                          ? <Link to={'/pokemon/' + w.formSlug} className="badge badge--form">als {w.form}</Link>
                          : <span className="badge badge--form">als {w.form}</span>
                      )}
                    </div>
                    <div className="card__meta">
                      {formatRange(w.startsAt, w.endsAt)}
                      {w.endsEstimated && <span className="card__meta-hinweis"> · Ende geschätzt</span>}
                    </div>
                    {w.region && <p className="card__summary" style={{ marginTop: 4 }}>Nur in: {w.region}</p>}
                    {w.note && <p className="card__summary" style={{ marginTop: 4 }}>{w.note}</p>}
                  </div>
                  {w.event && (
                    <Link to={`/event/${w.event.slug}`} className="badge badge--soft">{w.event.title}</Link>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {kommend.length > 0 && (
        <section className="section">
          <h2 className="section__title">Wann du es bekommst</h2>
          <div className="stack">
            {kommend.map((a) => <AuftrittZeile key={a.eventSlug + a.role} auftritt={a} />)}
          </div>
        </section>
      )}

      {vorbei.length > 0 && (
        <section className="section">
          <h2 className="section__title">Vergangene Auftritte</h2>
          <div className="stack">
            {vorbei.map((a) => <AuftrittZeile key={a.eventSlug + a.role} auftritt={a} vergangen />)}
          </div>
        </section>
      )}
    </>
  );
}

function AuftrittZeile({ auftritt, vergangen = false }) {  const akzent = auftritt.accentColor || `var(--ev-${auftritt.eventType}, var(--accent))`;

  return (
    <Link
      to={`/event/${auftritt.eventSlug}`}
      className={`admin-item${vergangen ? ' admin-item--draft' : ''}`}
    >
      <div>
        <div style={{ fontWeight: 700 }}>{auftritt.eventTitle}</div>
        <div className="card__meta">{formatRange(auftritt.startsAt, auftritt.endsAt)}</div>
        {auftritt.note && <p className="card__summary" style={{ marginTop: 4 }}>{auftritt.note}</p>}
      </div>
      <div className="inline">
        <span className="badge badge--soft">{POKEMON_ROLES[auftritt.role] ?? auftritt.role}</span>
        <span className="badge" style={{ '--badge-color': akzent }}>
          {EVENT_TYPES[auftritt.eventType] ?? EVENT_TYPES.other}
        </span>
      </div>
    </Link>
  );
}

/**
 * Ein Einsatzzweck mit seiner Einstufung.
 *
 * Eine leere Kachel ist eine Aussage: entweder spielt die Art in dieser Liga
 * keine Rolle, oder für Raids fehlt uns die Einstufung. Beides steht dort,
 * statt die Kachel wegzulassen — sonst müsste man raten, warum sie fehlt.
 */
function EignungFeld({ titel, hinweis, wert, raid = false }) {
  return (
    <div className={wert ? 'eignung__feld' : 'eignung__feld eignung__feld--leer'}>
      <div className="eignung__kopf">
        <span className="eignung__titel">{titel}</span>
        <span className="eignung__hinweis">{hinweis}</span>
      </div>

      {wert ? (
        <>
          <span className={'tier tier--' + wert.tier}>{wert.tier}</span>
          <span className="eignung__detail">
            {raid
              ? (wert.role === 'raid_boss' ? 'Als Boss' : 'Als Angreifer')
                + (wert.attackType ? ' · ' + wert.attackType : '')
              : 'Platz ' + wert.rankNo?.toLocaleString('de-DE')}
          </span>
          {wert.attackenKurz && (
            <span className="eignung__attacken">{wert.attackenKurz}</span>
          )}
        </>
      ) : (
        <span className="eignung__detail text-faint">
          {raid ? 'Nicht eingestuft' : 'Ohne Bedeutung'}
        </span>
      )}
    </div>
  );
}
